import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getCollection,
  refCollectionsOf,
  type CollectionDef,
  type CollectionField,
  type OrgMember,
  type OrgRecord,
  type Paginated,
  type RecordData,
} from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useOrg, type CurrentOrg } from '../../lib/auth.js';
import { Button, Dialog, Field, Input, Select, Textarea, useToast } from '../../components/ui.js';
import { missingRequired } from './format.js';
import './records.css';

interface RecordWizardProps {
  def: CollectionDef;
  /** null = create a new record. */
  record: OrgRecord | null;
  open: boolean;
  onClose: () => void;
  /**
   * Pre-filled answers for a NEW record (ignored when editing). Read when the
   * dialog opens, not tracked afterwards — callers pass inline objects.
   */
  initialData?: RecordData;
}

/** A record another record's `record` field may point at. */
export interface RecordOption {
  id: string;
  name: string;
  collection: string;
}

/** The statuses a `record` field may reference: live records and unapproved drafts. */
const REFERENCEABLE_STATUSES = ['active', 'draft'] as const;

/** The referenceable records, with the lists' own loading state. */
export interface RecordOptions {
  /** A list is still loading: a stored value cannot be judged orphaned yet. */
  pending: boolean;
  options: RecordOption[];
}

/**
 * Records of the given collections that a `record` field may reference,
 * sorted by name. One query per collection and status (the same keys the
 * collection list pages use, so the cache is shared). Called once at the
 * wizard's top level and handed down to every field control.
 */
export function useRecordOptions(
  org: CurrentOrg,
  collections: string[],
  { enabled }: { enabled: boolean },
): RecordOptions {
  const results = useQueries({
    queries: collections.flatMap((collection) =>
      REFERENCEABLE_STATUSES.map((status) => ({
        queryKey: ['records', org.orgId, collection, status],
        queryFn: () =>
          apiFetch<Paginated<OrgRecord>>(`/orgs/${org.orgId}/records`, {
            query: { collection, status, limit: 200 },
          }),
        enabled,
      })),
    ),
    combine: (queries) => ({
      pending: queries.some((q) => q.isPending),
      options: queries
        .flatMap((q) => q.data?.items ?? [])
        .map((r) => ({ id: r.id, name: r.name, collection: r.collection }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    }),
  });
  return results;
}

function FieldControl({
  field,
  value,
  onChange,
  members,
  recordOptions,
  recordOptionsPending,
  error,
}: {
  field: CollectionField;
  value: unknown;
  onChange: (value: unknown) => void;
  members: OrgMember[];
  recordOptions: RecordOption[];
  recordOptionsPending: boolean;
  error?: string;
}) {
  switch (field.type) {
    case 'textarea':
      return (
        <Field label={field.label} hint={field.hint} required={field.required} error={error}>
          {(props) => (
            <Textarea
              {...props}
              rows={3}
              value={(value as string) ?? ''}
              onChange={(e) => onChange(e.target.value)}
            />
          )}
        </Field>
      );
    case 'select':
    case 'member':
      return (
        <Field label={field.label} hint={field.hint} required={field.required} error={error}>
          {(props) => (
            <Select
              {...props}
              value={(value as string) ?? ''}
              onChange={(e) => onChange(e.target.value)}
            >
              <option value="">Select…</option>
              {field.type === 'member'
                ? members.map((m) => (
                    <option key={m.user.id} value={m.user.id}>
                      {m.user.name}
                    </option>
                  ))
                : (field.options ?? []).map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
            </Select>
          )}
        </Field>
      );
    case 'record': {
      const targets = field.refCollections ?? [];
      const options = recordOptions.filter((r) => targets.includes(r.collection));
      const current = typeof value === 'string' ? value : '';
      // An archived (or since-deleted) target is not offered, but the stored
      // value must still show as selected until the user changes it. While the
      // lists are still loading nothing is orphaned yet — a placeholder keeps
      // the stored value selected without mislabelling a perfectly good link.
      const orphaned = !recordOptionsPending && current !== '' && !options.some((r) => r.id === current);
      return (
        <Field label={field.label} hint={field.hint} required={field.required} error={error}>
          {(props) => (
            <Select {...props} value={current} onChange={(e) => onChange(e.target.value)}>
              <option value="">None</option>
              {recordOptionsPending && current !== '' && <option value={current}>Loading…</option>}
              {orphaned && <option value={current}>Current link (no longer available)</option>}
              {options.map((r) => (
                <option key={r.id} value={r.id}>
                  {targets.length > 1
                    ? `${r.name} (${getCollection(r.collection)?.singular ?? r.collection})`
                    : r.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      );
    }
    case 'boolean':
      return (
        <div className="field">
          <span className="field-label">
            {field.label}
            {field.required && (
              <span aria-hidden="true" style={{ color: 'var(--color-danger)' }}>
                {' '}
                *
              </span>
            )}
          </span>
          {field.hint && <span className="field-hint">{field.hint}</span>}
          <div className="radio-row" role="radiogroup" aria-label={field.label}>
            {[true, false].map((v) => (
              <label key={String(v)}>
                <input
                  type="radio"
                  name={`bool-${field.key}`}
                  checked={value === v}
                  onChange={() => onChange(v)}
                />
                {v ? 'Yes' : 'No'}
              </label>
            ))}
          </div>
          {error && (
            <span className="field-error" role="alert">
              {error}
            </span>
          )}
        </div>
      );
    case 'multiselect': {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      const toggle = (option: string, on: boolean) =>
        onChange(on ? [...selected, option] : selected.filter((s) => s !== option));
      return (
        <div className="field">
          <span className="field-label">
            {field.label}
            {field.required && (
              <span aria-hidden="true" style={{ color: 'var(--color-danger)' }}>
                {' '}
                *
              </span>
            )}
          </span>
          {field.hint && <span className="field-hint">{field.hint}</span>}
          <div className="check-grid" role="group" aria-label={field.label}>
            {(field.options ?? []).map((o) => (
              <label key={o}>
                <input
                  type="checkbox"
                  checked={selected.includes(o)}
                  onChange={(e) => toggle(o, e.target.checked)}
                />
                {o}
              </label>
            ))}
          </div>
          {error && (
            <span className="field-error" role="alert">
              {error}
            </span>
          )}
        </div>
      );
    }
    default: {
      const inputType =
        field.type === 'number'
          ? 'number'
          : field.type === 'date'
            ? 'date'
            : field.type === 'email'
              ? 'email'
              : field.type === 'url'
                ? 'url'
                : 'text';
      return (
        <Field label={field.label} hint={field.hint} required={field.required} error={error}>
          {(props) => (
            <Input
              {...props}
              type={inputType}
              placeholder={field.type === 'url' ? 'https://…' : undefined}
              value={value === undefined || value === null ? '' : String(value)}
              onChange={(e) =>
                onChange(
                  field.type === 'number'
                    ? e.target.value === ''
                      ? ''
                      : Number(e.target.value)
                    : e.target.value,
                )
              }
            />
          )}
        </Field>
      );
    }
  }
}

export function RecordWizard({ def, record, open, onClose, initialData }: RecordWizardProps) {
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [step, setStep] = useState(0);
  const [data, setData] = useState<RecordData>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset when (re)opened for a different record. `initialData` is read here
  // and deliberately not tracked: callers pass inline objects, and tracking
  // it would wipe the user's edits on every parent re-render.
  useEffect(() => {
    if (open) {
      setStep(0);
      setData(record?.data ?? initialData ?? {});
      setErrors({});
    }
  }, [open, record?.id]);

  const members = useQuery({
    queryKey: ['members', org.orgId],
    queryFn: () => apiFetch<OrgMember[]>(`/orgs/${org.orgId}/members`),
    enabled: open,
  });
  const refCollections = useMemo(() => refCollectionsOf(def), [def]);
  const { pending: recordOptionsPending, options: recordOptions } = useRecordOptions(
    org,
    refCollections,
    { enabled: open },
  );

  const save = useMutation({
    mutationFn: () =>
      record
        ? apiFetch<OrgRecord>(`/orgs/${org.orgId}/records/${record.id}`, {
            method: 'PATCH',
            body: { data },
          })
        : apiFetch<OrgRecord>(`/orgs/${org.orgId}/records`, {
            method: 'POST',
            body: { collection: def.key, data },
          }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['records', org.orgId] });
      toast(record ? 'Record updated.' : `${def.name}: record added.`, 'success');
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiRequestError && Array.isArray(err.details?.issues)) {
        const serverErrors: Record<string, string> = {};
        for (const issue of err.details.issues as Array<{ path: string; message: string }>) {
          serverErrors[issue.path] = issue.message;
        }
        setErrors(serverErrors);
        // Jump to the first step containing an error.
        const bad = def.steps.findIndex((s) => s.fields.some((f) => serverErrors[f.key]));
        if (bad >= 0) setStep(bad);
        toast('Please fix the highlighted fields.', 'error');
      } else {
        toast(err instanceof ApiRequestError ? err.message : 'Could not save the record', 'error');
      }
    },
  });

  const current = def.steps[step];
  const setValue = (key: string, value: unknown) => {
    setData((d) => ({ ...d, [key]: value }));
    setErrors((e) => {
      if (!e[key]) return e;
      const { [key]: _, ...rest } = e;
      return rest;
    });
  };

  const validateStep = (): boolean => {
    const stepErrors = missingRequired(current.fields, data);
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const next = () => {
    if (validateStep()) setStep((s) => Math.min(s + 1, def.steps.length - 1));
  };
  const submit = () => {
    if (validateStep()) save.mutate();
  };

  const title = useMemo(
    () => (record ? `Edit ${def.singular}` : `Add new ${def.singular}`),
    [record, def.singular],
  );

  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <div className="record-dialog">
        {def.steps.length > 1 && (
          <ol className="wizard-steps" style={{ listStyle: 'none', padding: 0 }}>
            {def.steps.map((s, i) => (
              <li key={s.key} style={{ display: 'contents' }}>
                <button
                  type="button"
                  className={`wizard-step ${i < step ? 'done' : ''}`}
                  aria-current={i === step ? 'step' : undefined}
                  onClick={() => {
                    // Moving forward validates each step in between.
                    if (i <= step || validateStep()) setStep(i);
                  }}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                >
                  <span className="step-dot" aria-hidden="true">
                    {i + 1}
                  </span>
                  {s.title}
                </button>
                {i < def.steps.length - 1 && <span className="wizard-connector" aria-hidden="true" />}
              </li>
            ))}
          </ol>
        )}

        {current.fields.map((field) => (
          <FieldControl
            key={field.key}
            field={field}
            value={data[field.key]}
            onChange={(v) => setValue(field.key, v)}
            members={members.data ?? []}
            recordOptions={recordOptions}
            recordOptionsPending={recordOptionsPending}
            error={errors[field.key]}
          />
        ))}

        <div className="dialog-actions">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {step > 0 && (
            <Button variant="secondary" onClick={() => setStep((s) => s - 1)}>
              Back
            </Button>
          )}
          {step < def.steps.length - 1 ? (
            <Button onClick={next}>Next</Button>
          ) : (
            <Button loading={save.isPending} onClick={submit}>
              {record ? 'Save changes' : `Add ${def.singular}`}
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
